import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

/** The per-route title the Topbar already renders. Routes declare it in
 *  `staticData`; see any file under src/routes. */
type RouteMeta = {
  titleKey?: string;
  title?: string;
};

/**
 * Keeps `<title>` on the current screen.
 *
 * It was the index.html string on all forty-three routes, so every tab, every
 * history entry and every bookmark of this app read "Axis Towers Wellness —
 * CRM" and nothing else. The titles themselves already existed — each route
 * declares `staticData.titleKey` for the Topbar — so this reads the same
 * source rather than introducing a second place to name a screen.
 *
 * Called once at the root, so it covers login and the public survey too.
 */
export function useDocumentTitle(): void {
  const { t, i18n } = useTranslation();
  const matches = useRouterState({ select: (s) => s.matches });

  // The innermost match that names itself: a leaf like /clients/$id inherits
  // its parent's title when it declares none of its own.
  const page = matches.reduceRight<string | undefined>((found, m) => {
    if (found !== undefined) return found;
    const meta = m.staticData as RouteMeta | undefined;
    if (meta?.titleKey !== undefined) return t(meta.titleKey);
    return meta?.title;
  }, undefined);

  useEffect(() => {
    document.title =
      page !== undefined && page !== "" ? t("app.titleWithPage", { page }) : t("app.title");
    // i18n.language is a dependency in substance: `t` is stable across a
    // language change, so without it the title keeps the old language.
  }, [page, t, i18n.language]);
}
